import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdmissionCard } from '@components/admission-card';
import {
  ADMISSION_CARD_TAG_ACCESS_CODE,
  ADMISSION_CARD_TAG_IMPORT_ONLY,
  AdmissionCardTag,
} from '@components/admission-card/src/admission-card.type';
import { AdmissionDeleteSoldItemsModal } from '@components/admission-delete-sold-items-modal';
import { ButtonSecondary } from '@components/button';
import { ActionItemButton } from '@components/button/src/action-button';
import {
  ConfirmModal,
  isMobileImplicitDismiss,
  Modal,
  type ModalCloseSource,
  PresetModal,
  SelectOfferPresetModal,
} from '@components/modals';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import {
  closestCenter,
  DndContext,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  BottleServicePreset,
  CreateAdmissionPresetRequest,
  Preset,
  PRESET_TYPE_BOTTLE_SERVICE,
  PresetType,
} from '@domain/preset';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { useCreateAdmissionPreset } from '@hooks/presets/admissions/use-create-admission-preset';
import { useDeleteAdmissionPreset } from '@hooks/presets/admissions/use-delete-admission-preset';
import { useEditAdmissionPreset } from '@hooks/presets/admissions/use-edit-admission-preset';
import { useUnassignBottleServiceFloorplan } from '@hooks/presets/floorplan/use-unasign-bottle-sercice';
import { useGetFormFieldsPresets } from '@hooks/use-form-fields-presets/use-form-fields-presets';
import { getAdmissionDDPPrice } from '@utils/get-admission-ddp-price/get-admission-ddp-price';
import { getAdmissionOnlinePrice } from '@utils/get-admission-online-price/get-admission-online-price';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

import { Props } from './assign-offers-to-floorplan-preset.type';

export const AssignOffersToFloorplanPreset = ({
  floorplan,
  admissions,
  assignedOffers,
  floorplanAdmissions,
  setAssignedOffers,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { clubState } = useContext(CompanyClubContext);
  const [selectedOffer, setSelectedOffer] = useState<{
    preset: Preset;
    type: PresetType;
  } | null>(null);
  const [isSelectOfferModalOpen, setIsSelectOfferModalOpen] = useState(false);
  const [isAdmissionModalOpen, setIsAdmissionModalOpen] = useState(false);
  const [isHideDeleteModalOpen, setIsHideDeleteModalOpen] = useState(false);
  const [isUnassignModalOpen, setIsUnassignModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [activeConfirmModal, setActiveConfirmModal] = useState<boolean>(false);
  const [isCreatingOffer, setIsCreatingOffer] = useState<boolean>(false);
  const [items, setItems] = useState<BottleServicePreset[]>([]);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const { mutate: unassignOffer } = useUnassignBottleServiceFloorplan(floorplan.id);
  const { mutate: deleteOffer } = useDeleteAdmissionPreset({});
  const { data: formFieldsPresets, isLoading: isLoadingFormFields } = useGetFormFieldsPresets();
  const { mutate: editOffer } = useEditAdmissionPreset({
    floorplanId: floorplan.id,
  });
  const { mutate: createOffer, data: offerCreated } = useCreateAdmissionPreset({
    floorplanId: floorplan.id,
  });

  const filteredAdmissions = admissions.bottleService.filter((admission) => assignedOffers.includes(admission.id));

  const allFloorplanAdmissions = [...(floorplanAdmissions?.bottleService ?? []), ...filteredAdmissions];

  const floorplanAdmissionsId = allFloorplanAdmissions.map((admission) => admission.id);

  const areBottleServiceToAssign = admissions.bottleService.filter(
    (bottleService) => !floorplanAdmissionsId.includes(bottleService.id)
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      // Disable smooth scrolling in Cypress automated tests
      scrollBehavior: 'Cypress' in globalThis ? 'auto' : undefined,
    })
  );

  const getIndex = (id: UniqueIdentifier) => items.findIndex((item: { id: UniqueIdentifier }) => item.id === id);

  const activeIndex = activeId ? getIndex(activeId) : -1;

  const handleEdit = (preset: Preset, type: PresetType) => {
    setSelectedOffer({ preset, type });
    setIsAdmissionModalOpen(true);
  };

  const handleUnassign = (preset: Preset, type: PresetType) => {
    setSelectedOffer({ preset, type });
    setIsUnassignModalOpen(true);
  };

  const handleDelete = (preset: Preset, type: PresetType) => {
    setSelectedOffer({ preset, type });
    setIsDeleteModalOpen(true);
  };

  const handleSelectOfferModalClose = () => {
    setIsSelectOfferModalOpen(false);
  };

  const handleCloseHideDeleteModal = () => {
    setIsHideDeleteModalOpen(false);
    setSelectedOffer(null);
  };

  const hideOffer = () => {
    if (!selectedOffer) {
      return;
    }

    setAssignedOffers((prev) => prev.filter((id) => id !== selectedOffer.preset.id));
    setSelectedOffer(null);
    setIsHideDeleteModalOpen(false);
  };

  const handleUnassignOffer = () => {
    if (!selectedOffer) {
      return;
    }

    unassignOffer({ admissionPresetId: selectedOffer.preset.id });

    setItems(items.filter((item) => item.id !== selectedOffer.preset.id));
    setAssignedOffers((prev) => prev.filter((id) => id !== selectedOffer.preset.id));
    setSelectedOffer(null);
    setIsUnassignModalOpen(false);
  };

  const handleDeleteOffer = () => {
    if (!selectedOffer) {
      return;
    }

    deleteOffer(
      {
        presetId: selectedOffer.preset.id,
        type: selectedOffer.type,
      },
      {
        onSuccess: () => {
          setItems(items.filter((item) => item.id !== selectedOffer.preset.id));
          setAssignedOffers((prev) => prev.filter((id) => id !== selectedOffer.preset.id));
          setSelectedOffer(null);
          setIsDeleteModalOpen(false);
        },
        onError: () => {
          setIsDeleteModalOpen(false);
          handleUnassignOffer();
        },
      }
    );
  };

  const handleCloseUnassignModal = () => {
    setIsUnassignModalOpen(false);
    setSelectedOffer(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedOffer(null);
  };

  const handleOpenCreateEditModal = () => {
    setIsCreatingOffer(true);
    setSelectedOffer(null);
    setIsAdmissionModalOpen(true);
  };

  const handleCloseCreateEditModal = (source?: ModalCloseSource) => {
    if (activeConfirmModal && !isMobileImplicitDismiss(source)) setIsConfirmModalOpen(true);
    else setIsAdmissionModalOpen(false);
  };

  const handleClickOutsideCreateEditModal = (source?: ModalCloseSource) => {
    handleCloseCreateEditModal(source);
  };

  const handleSubmitCreateNewOffer = (preset: CreateAdmissionPresetRequest, type: PresetType) => {
    if (!selectedOffer) {
      const admissionWithFloorplanId = {
        ...preset,
        floorplanPresetsId: [floorplan.id],
      };
      createOffer({ preset: admissionWithFloorplanId, type });
      setIsAdmissionModalOpen(false);
      return;
    }

    editOffer({ preset, type, presetId: selectedOffer.preset.id });
    setIsAdmissionModalOpen(false);
  };

  const handleSaveConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setIsAdmissionModalOpen(false);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setIsCreatingOffer(false);
  };

  useEffect(() => {
    setAssignedOffers(items.map((item) => item.id));
  }, [items, setAssignedOffers]);

  useEffect(() => {
    if (!isCreatingOffer) setItems(floorplanAdmissions?.bottleService ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorplanAdmissions?.bottleService]);

  useEffect(() => {
    if (offerCreated) {
      setItems([...items, offerCreated as BottleServicePreset]);
      setIsCreatingOffer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerCreated, setItems, setIsCreatingOffer]);

  const addPresetBottleService = (bottleService: BottleServicePreset) => {
    setItems([...items, bottleService]);
  };

  return (
    <>
      <div className='flex flex-col items-start md:items-center px-7 py-8 md:flex-row md:justify-between gap-4'>
        <div className='text-left'>
          <p className='text-primary text-subtitle-s md:text-subtitle-m'>{t('floorplan:offersAndPricing')}</p>
          <p className='text-primary text-body-xs md:text-body-s'>
            {t('floorplan:assignAdmissionsAndPricingOffersToDifferentZones')}
          </p>
        </div>
        <Menu as='div' className='relative'>
          {({ open }) => (
            <>
              <MenuButton>
                <ButtonSecondary>{t('floorplan:assignAdmission')}</ButtonSecondary>
              </MenuButton>
              <Transition
                show={open}
                as={Fragment}
                enter='transition ease-out duration-100'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='transition ease-in duration-75'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'>
                <MenuItems
                  modal={false}
                  anchor={{ to: 'bottom end', gap: 16 }}
                  className='z-10 w-56 flex flex-col gap-2 p-4 origin-top-right bg-surface-primary rounded-md shadow-sm overflow-auto focus:outline-hidden'>
                  <MenuItem>
                    {({ focus }) => (
                      <ActionItemButton
                        active={focus}
                        onClick={handleOpenCreateEditModal}
                        text={t('floorplan:createNew')}
                      />
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <ActionItemButton
                        active={active}
                        onClick={() => setIsSelectOfferModalOpen(true)}
                        text={t('offers:assignExistingPreset')}
                        disabled={areBottleServiceToAssign.length === 0}
                      />
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </>
          )}
        </Menu>
      </div>

      <div className='px-7 py-8 space-y-6 text-left'>
        <DndContext
          collisionDetection={closestCenter}
          sensors={sensors}
          onDragStart={({ active }: DragStartEvent) => {
            setActiveId(active.id);
          }}
          onDragEnd={({ over }) => {
            setActiveId(null);
            if (over) {
              const overIndex = getIndex(over.id);
              if (activeIndex !== overIndex) {
                if (activeId) setItems((sortedItems) => arrayMove(sortedItems, activeIndex, overIndex));
              }
            }
          }}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragCancel={() => setActiveId(null)}>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((offer: Preset) => {
              const defaultOfferNameTranslation = getDefaultOfferNameTranslation(PRESET_TYPE_BOTTLE_SERVICE);
              const onlinePrice = getAdmissionOnlinePrice(offer);
              const ddpPrice = getAdmissionDDPPrice(offer);
              const offerName =
                getTranslatedPropertyBasedOnOrder({
                  translations: offer.name,
                  preferredLanguage: i18n.language,
                }) ?? t(defaultOfferNameTranslation);

              const offerDescription = getTranslatedPropertyBasedOnOrder({
                translations: offer.description,
                preferredLanguage: i18n.language,
              });
              let tags: AdmissionCardTag[] = offer.accessCode ? [ADMISSION_CARD_TAG_ACCESS_CODE] : [];

              if (offer.settings.isPrivate) tags = [...tags, ADMISSION_CARD_TAG_IMPORT_ONLY];
              return (
                <AdmissionCard
                  key={offer.id}
                  id={offer.id}
                  isDragActive={true}
                  timezone={clubState.city.timezone}
                  color={offer.color ?? ''}
                  name={offerName ?? ''}
                  description={offerDescription ?? ''}
                  quantity={offer.quantity}
                  soldItems={0}
                  currency={offer.price.currency}
                  amount={offer.price.amount}
                  onlinePrice={onlinePrice}
                  ddpPrice={ddpPrice}
                  isPrivate={offer.settings.isPrivate}
                  needsUserVerified={false}
                  isPublished={false}
                  salesEnds={0}
                  tags={tags}
                  onEdit={() => handleEdit(offer, 'bottleService')}
                  onDelete={() => handleDelete(offer, 'bottleService')}
                  onUnassign={() => handleUnassign(offer, 'bottleService')}
                  guestCapacity={(offer as BottleServicePreset).guestCapacity}
                  deleteIsUnassign={true}
                  isPreset={true}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>

      <Modal isOpen={isAdmissionModalOpen} handleClose={handleClickOutsideCreateEditModal}>
        {formFieldsPresets && (
          <PresetModal
            isOpen={isAdmissionModalOpen && !isLoadingFormFields}
            handleClose={handleCloseCreateEditModal}
            handleSubmit={handleSubmitCreateNewOffer}
            preset={selectedOffer?.preset}
            floorplan={floorplan}
            presetType={PRESET_TYPE_BOTTLE_SERVICE}
            formFieldsPresets={formFieldsPresets}
            onDeleteOffer={handleUnassignOffer}
            handleSaveConfirmModal={handleSaveConfirmModal}
            handleCloseConfirmModal={handleCloseConfirmModal}
            isConfirmModalOpen={isConfirmModalOpen}
            setActiveConfirmModal={setActiveConfirmModal}
          />
        )}
      </Modal>

      <Modal variant='md' isOpen={isSelectOfferModalOpen} handleClose={handleSelectOfferModalClose}>
        {isSelectOfferModalOpen && (
          <SelectOfferPresetModal
            handleClose={handleSelectOfferModalClose}
            admissions={admissions}
            floorplanAdmissionsId={floorplanAdmissionsId}
            addPresetBottleService={addPresetBottleService}
          />
        )}
      </Modal>

      <Modal isOpen={isHideDeleteModalOpen} handleClose={handleCloseHideDeleteModal} variant='sm'>
        <AdmissionDeleteSoldItemsModal handleClose={handleCloseHideDeleteModal} handleClick={hideOffer} />
      </Modal>

      <ConfirmModal
        isOpen={isUnassignModalOpen}
        title={t('offers:admissionUnassignTitle')}
        handleClose={handleCloseUnassignModal}
        handleClickConfirm={handleUnassignOffer}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title={t('offers:admissionDeleteTitle')}
        handleClose={handleCloseDeleteModal}
        handleClickConfirm={handleDeleteOffer}
      />
    </>
  );
};
